// AI Generated Service
import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { PagePerformanceCategoryService } from '@client/app/modules/page-performance/services/category/category.service';
import { PAGE_PERFORMANCE_CATEGORY } from '@client/app/modules/page-performance/constants/page-performance-category';
import { PAGE_PERFORMANCE_AUDIT_ID } from '@client/app/modules/page-performance/constants/page-performance-audit-id';
import { PagePerformancePageDetailsService } from '@client/app/modules/page-performance/services/page-performance-page-details/page-performance-page-details.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PagePerformanceService } from '@client/app/modules/page-performance/services/page-performance/page-performance.service';
import { PagePerformanceProfileObsService } from '@client/app/modules/page-performance/services/profile-obs/profile-obs.service';
import { BehaviorSubject } from 'rxjs';

/**
 * Service for the Performance Pages component
 */
@Injectable({
    providedIn: 'root',
})
export class PerformancePagesService {
    assignedProfilesObs = new BehaviorSubject<any>([]);
    private assignedProfiles: any[] = [];
    private profiles: { [key: string]: any } = {};

    constructor (
        private translateService: TranslateService,
        private monEventsService: MonEventService,
        private pagePerformanceCategoryService: PagePerformanceCategoryService,
        private pagePerformancePageDetailsService: PagePerformancePageDetailsService,
        private sessionService: SessionService,
        private pagePerformanceService: PagePerformanceService,
        private pagePerformanceProfileObsService: PagePerformanceProfileObsService,
    ) {
        this.init();
    }

    private init (): void {
        this.pagePerformanceProfileObsService.profilesObs.subscribe((profiles) => {
            this.profiles = {};
            profiles.map((profile) => {
                this.profiles[profile.id] = profile;
            });
        });

        this.monEventsService.addListener('pagePerformanceUpdateAssignedProfile' as any, (page: any, profile: any) => {
            const index = this.assignedProfiles.findIndex((assignedProfile) => {
                return (
                    assignedProfile.page &&
                    page.id === assignedProfile.page.id &&
                    this.getDefaultProfileIdFromAssignedProfile(assignedProfile) === profile.id
                );
            });

            if (index > -1) {
                const report = this.pagePerformancePageDetailsService.getReport();
                const seoCategory = this.pagePerformanceCategoryService.getCategory(PAGE_PERFORMANCE_CATEGORY.SEO);
                const performanceCategory = this.pagePerformanceCategoryService.getCategory(PAGE_PERFORMANCE_CATEGORY.PERFORMANCE);
                const bestPracticeCategory = this.pagePerformanceCategoryService.getCategory(PAGE_PERFORMANCE_CATEGORY.BEST_PRACTICE);
                const audits = this.pagePerformanceCategoryService.getAuditsFromCategory(PAGE_PERFORMANCE_CATEGORY.PERFORMANCE);
                const screenshotThumbnailsAudit = audits?.find((audit) => audit?.id === PAGE_PERFORMANCE_AUDIT_ID.FINAL_SCREENSHOT);
                const metrics = audits?.find((audit) => audit?.id === PAGE_PERFORMANCE_AUDIT_ID.METRICS);
                const metricsItemDetail = (metrics?.details as any)?.items?.[0];

                // NB! Consider using a less static way of assigning key values directly on a Assigned Profile
                this.assignedProfiles[index].screenshot = (screenshotThumbnailsAudit?.details as any)?.data;
                this.assignedProfiles[index].scores = {
                    profile_id: profile.id,
                    scanned_at: report?.fetchTime,
                    performance: performanceCategory?.score,
                    seo: seoCategory?.score,
                    best_practices: bestPracticeCategory?.score,
                    first_contentful_paint: metricsItemDetail?.firstContentfulPaint,
                    first_meaningful_paint: metricsItemDetail?.firstMeaningfulPaint,
                    time_to_interactive: metricsItemDetail?.interactive,
                };
            }
        });
    }

    /**
     * Get Profiles collection
     */
    getProfiles (): { [key: string]: any } {
        return this.profiles;
    }

    /**
     * Set the Assigned Profiles Collection
     */
    setAssignedProfiles (assignedProfiles: any[]): void {
        this.assignedProfiles = assignedProfiles;
        this.assignedProfilesObs.next(this.assignedProfiles);
    }

    /**
     * Get filtered Assigned Profiles Collection
     */
    getAssignedProfiles (): any[] {
        return this.assignedProfiles;
    }

    /**
     * Get Name of the Default related Profile on the Assigned Profile
     */
    getProfileDeviceName (assignedProfile: any): string {
        const profileId = this.getDefaultProfileIdFromAssignedProfile(assignedProfile);
        const profile = Object.values(this.profiles).find((p: any) => p.id === profileId);
        if (profile) {
            return profile.getDeviceName();
        }

        return this.translateService.getString('Device not found');
    }

    /**
     * Get Location of the Default related Profile on the Assigned Profile
     */
    getProfileLocation (assignedProfile: any): string {
        const profileId = this.getDefaultProfileIdFromAssignedProfile(assignedProfile);
        const profile = Object.values(this.profiles).find((p: any) => p.id === profileId);
        if (profile) {
            return profile.getCountry() + ': ' + profile.getCity();
        }

        return this.translateService.getString('Location not found');
    }

    /**
     * Get Country Code of the Default related Profile on the Assigned Profile
     */
    getProfileCountryCode (assignedProfile: any): string {
        const profileId = this.getDefaultProfileIdFromAssignedProfile(assignedProfile);
        const profile = Object.values(this.profiles).find((p: any) => p.id === profileId);
        if (profile) {
            return profile.getCountryCode();
        }

        return '';
    }

    /**
     * Get Connection Name of the Default related Profile on the Assigned Profile
     */
    getProfileConnectionName (assignedProfile: any): string {
        const profileId = this.getDefaultProfileIdFromAssignedProfile(assignedProfile);
        const profile = Object.values(this.profiles).find((p: any) => p.id === profileId);
        if (profile) {
            return profile.getConnectionName();
        }

        return this.translateService.getString('Network not found');
    }

    /**
     * Check if the Customer is allowed to rescan a page on demand
     */
    canRescan (): boolean {
        return Boolean(this.sessionService.customer !== null && this.sessionService.customer.plan_traits.performance_tracking_on_demand_scan);
    }

    /**
     * Get the number of maximum allowed pages for this account
     */
    getMaxPageCount (): number {
        return this.pagePerformanceService.getMaxPageCount();
    }

    /**
     * Get the Default ID from the Assigned Profile
     */
    private getDefaultProfileIdFromAssignedProfile (assignedProfile: any): number {
        return assignedProfile.default_profile_id || assignedProfile.profile_ids[0];
    }
}
